/**
 * The event's issuances as CSV (LIM-4707): what "Copy as CSV" puts on the
 * clipboard at close, for settling the venue's bill in a spreadsheet. One row
 * per guest handed tickets, most recent first as the server lists them.
 */

import type { EventTicketIssuance } from '@/api/types';
import { escapeCsvCell } from '@/lib/csv';

const HEADER = ['Issued at', 'Guest', 'Tag', 'Tickets', 'Issued by', 'User ID'] as const;

/**
 * When the tickets were handed over, as "2026-10-31 21:14" in this device's
 * timezone: the venue's, for staff settling the bill there. Year first, so a
 * spreadsheet reads it as a date and sorts it.
 */
export function csvTimeText(iso: string): string {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) throw new Error(`${iso} is not a time`);
  const parts = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(time);
  const part = (type: Intl.DateTimeFormatPartTypes): string => {
    const found = parts.find((p) => p.type === type);
    if (found === undefined) throw new Error(`${iso} formatted without a ${type}`);
    return found.value;
  };
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`;
}

/**
 * A spreadsheet runs a cell that starts with =, +, -, @, a tab or a carriage
 * return as a formula. A guest's name and tag are theirs to choose, so such a
 * cell is opened with a quote, which the spreadsheet shows as plain text.
 */
function textCell(value: string): string {
  return escapeCsvCell(/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);
}

/**
 * RFC 4180, CRLF after every record, as the users export writes it. A guest
 * the profile service no longer has comes with no name or tag, so those
 * cells are empty.
 */
export function buildEventIssuancesCsv(issuances: readonly EventTicketIssuance[]): string {
  const rows = issuances.map((issuance) =>
    [
      csvTimeText(issuance.issueTime),
      textCell(issuance.guestDisplayName ?? ''),
      textCell(issuance.guestDisplayTag ?? ''),
      String(issuance.ticketCount),
      textCell(issuance.actor),
      escapeCsvCell(issuance.userId),
    ].join(','),
  );
  return [HEADER.join(','), ...rows].map((row) => `${row}\r\n`).join('');
}
